import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  BarChart3, Plus, Eye, MousePointer, TrendingUp, Clock,
  CheckCircle2, Calendar, Mail, ExternalLink, Upload, X, CreditCard
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter
} from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import SEO from "../components/SEO";
import { API, useAuth } from "../App";
import { formatApiError } from "../lib/utils";
import { startCheckout } from "../lib/checkout";
import { FieldError } from "../components/FieldError";
import { uploadImage } from "../components/CompanyLogoField";

const AD_PRICE_HINT = "1,500";

const STATUS_STYLES = {
  pending: "bg-yellow-900/40 text-yellow-300 border-yellow-800",
  approved: "bg-emerald-900/40 text-emerald-300 border-emerald-800",
  rejected: "bg-red-900/40 text-red-300 border-red-800",
  paused: "bg-zinc-800 text-zinc-400 border-zinc-700",
};

export default function AdvertiserDashboard() {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [stats, setStats] = useState(null);
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedBanner, setSelectedBanner] = useState(null);
  const [selectedStats, setSelectedStats] = useState(null);
  const [showCreate, setShowCreate] = useState(false);

  useEffect(() => {
    if (!authLoading && !user) {
      toast.error("Please log in");
      navigate("/login");
    } else if (user && user.user_type !== "advertiser" && !user.is_admin) {
      toast.error("This dashboard is for advertisers");
      navigate("/dashboard");
    }
  }, [user, authLoading, navigate]);

  useEffect(() => {
    if (user) refreshData();
  }, [user]);

  const refreshData = async () => {
    setLoading(true);
    try {
      const [sRes, bRes] = await Promise.all([
        axios.get(`${API}/advertiser/stats`, { withCredentials: true }),
        axios.get(`${API}/advertiser/banners`, { withCredentials: true }),
      ]);
      setStats(sRes.data);
      setBanners(bRes.data || []);
    } catch (e) {
      toast.error(formatApiError(e, "Failed to load data"));
    } finally {
      setLoading(false);
    }
  };

  const loadBannerStats = async (banner) => {
    setSelectedBanner(banner);
    setSelectedStats(null);
    try {
      const r = await axios.get(`${API}/advertiser/banners/${banner.ad_id}/stats?days=30`, { withCredentials: true });
      setSelectedStats(r.data);
    } catch {
      toast.error("Failed to load stats");
    }
  };

  const requestEmailReport = async (banner) => {
    try {
      const r = await axios.post(`${API}/advertiser/banners/${banner.ad_id}/email-report`, {}, { withCredentials: true });
      if (r.data.status === "success") {
        toast.success("Report sent by email!");
      } else if (r.data.status === "skipped") {
        toast.info("Email skipped — admin needs to configure email service (RESEND_API_KEY).");
      } else {
        toast.error(r.data.error || "Failed to send report");
      }
    } catch (e) {
      toast.error(formatApiError(e, "Failed"));
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background py-12 px-4 md:px-8" data-testid="advertiser-dashboard">
      <SEO url="/advertiser" title="Advertiser Dashboard" description="Manage your banner ads and view performance stats." />

      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
          <div>
            <h1 className="font-heading text-4xl uppercase" data-testid="advertiser-header">
              ADVERTISER <span className="text-orange-500">DASHBOARD</span>
            </h1>
            <p className="text-zinc-500 mt-1">
              Hi {user?.name} — manage your banner ads and track performance
            </p>
          </div>
          <Button
            onClick={() => setShowCreate(true)}
            className="bg-orange-600 hover:bg-orange-500"
            data-testid="new-banner-btn"
          >
            <Plus className="h-4 w-4 mr-2" />
            Submit New Banner
          </Button>
        </div>

        {/* Stat Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8" data-testid="advertiser-stats">
            <StatCard icon={BarChart3} label="Total Banners" value={stats.total_banners} />
            <StatCard icon={CheckCircle2} label="Active" value={stats.active} accent="text-emerald-400" />
            <StatCard icon={Eye} label="Impressions" value={stats.total_impressions?.toLocaleString?.()} />
            <StatCard icon={MousePointer} label={`Clicks · CTR ${stats.ctr}%`} value={stats.total_clicks?.toLocaleString?.()} accent="text-orange-400" />
          </div>
        )}

        {/* Banners */}
        <h2 className="font-heading text-2xl uppercase mb-4">My Banners</h2>
        {banners.length === 0 ? (
          <div className="bg-zinc-900 border border-zinc-800 p-10 text-center" data-testid="no-banners">
            <BarChart3 className="h-10 w-10 text-zinc-700 mx-auto mb-3" />
            <p className="text-zinc-500 mb-4">You haven't submitted any banner yet.</p>
            <Button onClick={() => setShowCreate(true)} className="bg-orange-600 hover:bg-orange-500">
              Submit your first banner
            </Button>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-4" data-testid="banner-list">
            {banners.map((b) => (
              <div
                key={b.ad_id}
                className="bg-zinc-900 border border-zinc-800 hover:border-zinc-700 p-4 flex gap-4 cursor-pointer"
                data-testid={`banner-card-${b.ad_id}`}
                onClick={() => loadBannerStats(b)}
              >
                <img src={b.image_url || b.logo_url} alt={b.title} className={`w-28 h-20 flex-shrink-0 bg-zinc-800 ${b.image_url ? "object-cover" : "object-contain p-4 bg-white"}`} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <h3 className="font-heading text-lg truncate">{b.company_name ? `${b.company_name} · ` : ""}{b.title}</h3>
                    <Badge className={`border ${STATUS_STYLES[b.status] || STATUS_STYLES.paused}`}>
                      {b.status}
                    </Badge>
                  </div>
                  <p className="text-xs text-zinc-500 mt-1 flex items-center gap-1">
                    <Calendar className="h-3 w-3" />
                    {b.start_date?.slice(0, 10)} → {b.end_date?.slice(0, 10)}
                  </p>
                  <div className="flex items-center gap-4 mt-2 text-sm">
                    <span className="text-zinc-400"><Eye className="h-3 w-3 inline mr-1" />{b.impressions?.toLocaleString?.() || 0}</span>
                    <span className="text-zinc-400"><MousePointer className="h-3 w-3 inline mr-1" />{b.clicks?.toLocaleString?.() || 0}</span>
                    <span className="text-xs text-zinc-500 uppercase tracking-wider">{b.placement}</span>
                  </div>
                  {b.rejection_reason && (
                    <p className="text-xs text-red-400 mt-2">Reason: {b.rejection_reason}</p>
                  )}
                  <PaymentRow banner={b} isOwner={b.advertiser_id === user?.user_id} />
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Stats Drawer (Dialog) */}
        <Dialog open={!!selectedBanner} onOpenChange={(open) => !open && setSelectedBanner(null)}>
          <DialogContent className="bg-zinc-950 border-zinc-800 max-w-3xl max-h-[85vh] overflow-auto">
            <DialogHeader>
              <DialogTitle className="text-white">{selectedBanner?.title}</DialogTitle>
            </DialogHeader>
            {selectedStats ? (
              <div className="space-y-4">
                <div className="grid grid-cols-3 gap-3">
                  <StatCard icon={Eye} label="Impressions (30d)" value={selectedStats.totals.impressions?.toLocaleString?.()} />
                  <StatCard icon={MousePointer} label="Clicks (30d)" value={selectedStats.totals.clicks?.toLocaleString?.()} />
                  <StatCard icon={TrendingUp} label="CTR" value={`${selectedStats.totals.ctr}%`} accent="text-orange-400" />
                </div>
                <div>
                  <h4 className="text-sm uppercase text-zinc-400 mb-2">Daily breakdown</h4>
                  <div className="space-y-0.5">
                    {selectedStats.daily.slice(-14).reverse().map((d) => (
                      <div key={d.date} className="flex items-center gap-2 text-xs py-1 border-b border-zinc-900">
                        <span className="text-zinc-500 w-24">{d.date}</span>
                        <div className="flex-1 h-2 bg-zinc-900 rounded overflow-hidden">
                          <div
                            className="h-full bg-orange-600"
                            style={{ width: `${Math.min(100, (d.impressions / Math.max(1, Math.max(...selectedStats.daily.map((x) => x.impressions)))) * 100)}%` }}
                          />
                        </div>
                        <span className="w-16 text-right text-zinc-300">{d.impressions}</span>
                        <span className="w-16 text-right text-orange-400">{d.clicks}</span>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="flex gap-2 pt-3 border-t border-zinc-800">
                  <Button
                    variant="outline"
                    className="border-zinc-700"
                    onClick={() => requestEmailReport(selectedBanner)}
                    data-testid="email-report-btn"
                  >
                    <Mail className="h-4 w-4 mr-2" /> Email me last month's report
                  </Button>
                  <a
                    href={selectedBanner.link_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center text-sm text-zinc-400 hover:text-orange-400"
                  >
                    <ExternalLink className="h-4 w-4 mr-1" /> View destination
                  </a>
                </div>
              </div>
            ) : (
              <div className="p-4 text-center text-zinc-500">Loading stats…</div>
            )}
          </DialogContent>
        </Dialog>

        {/* Create Banner Dialog */}
        <CreateBannerDialog
          open={showCreate}
          onClose={() => setShowCreate(false)}
          onCreated={() => {
            setShowCreate(false);
            refreshData();
          }}
        />
      </div>
    </div>
  );
}

function PaymentRow({ banner, isOwner }) {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  if (banner.payment_status === "partner") {
    return <p className="text-xs text-zinc-500 mt-2" data-testid={`banner-payment-${banner.ad_id}`}>Partner advertiser</p>;
  }
  if (banner.payment_status === "paid") {
    return <p className="text-xs text-emerald-400 mt-2" data-testid={`banner-payment-${banner.ad_id}`}>Paid R{Number(banner.amount_paid || banner.price || 0).toFixed(2)}{banner.status === "pending" ? " · awaiting approval" : ""}</p>;
  }
  if (!isOwner || banner.status === "rejected") return null;
  return (
    <Button
      size="sm"
      className="mt-2 bg-orange-600 hover:bg-orange-500"
      disabled={busy}
      onClick={async (e) => {
        e.stopPropagation();
        setBusy(true);
        await startCheckout(navigate, { kind: "banner", ad_id: banner.ad_id });
        setBusy(false);
      }}
      data-testid={`pay-banner-${banner.ad_id}`}
    >
      <CreditCard className="h-4 w-4 mr-2" />
      {busy ? "Starting checkout…" : `Pay R${Number(banner.price || 0).toFixed(2)}`}
    </Button>
  );
}

function StatCard({ icon: Icon, label, value, accent = "text-white" }) {
  return (
    <div className="bg-zinc-900 border border-zinc-800 p-4">
      <Icon className="h-5 w-5 text-zinc-500 mb-2" />
      <div className="text-xs uppercase tracking-wider text-zinc-500">{label}</div>
      <div className={`font-heading text-2xl ${accent}`}>{value ?? "—"}</div>
    </div>
  );
}

function CreateBannerDialog({ open, onClose, onCreated }) {
  const [form, setForm] = useState({
    title: "",
    company_name: "",
    image_url: "",
    link_url: "",
    placement: "browse",
    start_date: new Date().toISOString().slice(0, 10),
    end_date: new Date(Date.now() + 30 * 86400000).toISOString().slice(0, 10),
    contact_email: "",
  });
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [errors, setErrors] = useState({});
  const update = (field, value) => {
    setForm((f) => ({ ...f, [field]: value }));
    setErrors((e) => ({ ...e, [field]: undefined, dates: undefined }));
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploading(true);
    try {
      const url = await uploadImage(file);
      update("image_url", url);
      toast.success("Image uploaded");
    } catch (err) {
      setErrors((x) => ({ ...x, image_url: err.message }));
    } finally {
      setUploading(false);
    }
  };

  const validate = () => {
    const errs = {};
    if (!form.title.trim()) errs.title = "Please enter a campaign title";
    if (!form.image_url) errs.image_url = "Please upload a banner image";
    if (!form.link_url.trim()) errs.link_url = "Please enter the destination URL";
    else if (!/^https?:\/\/\S+\.\S+/.test(form.link_url.trim())) errs.link_url = "Enter a full link starting with https://";
    if (form.contact_email && !/^\S+@\S+\.\S+$/.test(form.contact_email)) errs.contact_email = "Enter a valid email address";
    if (!form.start_date || !form.end_date) errs.dates = "Please choose start and end dates";
    else if (form.end_date <= form.start_date) errs.dates = "End date must be after the start date";
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setSubmitting(true);
    try {
      await axios.post(
        `${API}/advertiser/banners`,
        {
          ...form,
          start_date: new Date(form.start_date).toISOString(),
          end_date: new Date(form.end_date).toISOString(),
          contact_email: form.contact_email || null,
        },
        { withCredentials: true }
      );
      toast.success("Banner submitted — pay now to send it for approval");
      onCreated();
    } catch (e) {
      toast.error(formatApiError(e, "Submit failed"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-zinc-950 border-zinc-800 max-w-xl max-h-[90vh] overflow-auto">
        <DialogHeader>
          <DialogTitle className="text-white">Submit a New Banner</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-zinc-300">Campaign Title *</Label>
            <Input
              className="bg-zinc-900 border-zinc-800 mt-1"
              value={form.title}
              onChange={(e) => update("title", e.target.value)}
              data-testid="banner-title-input"
              placeholder="e.g., Summer Sale 2026"
            />
            <FieldError message={errors.title} testId="banner-title-error" />
          </div>
          <div>
            <Label className="text-zinc-300">Company Name</Label>
            <Input
              className="bg-zinc-900 border-zinc-800 mt-1"
              value={form.company_name}
              onChange={(e) => update("company_name", e.target.value)}
              placeholder="Your brand name"
            />
          </div>
          <div>
            <Label className="text-zinc-300">Banner Image *</Label>
            <div className="mt-1 flex items-center gap-3">
              <input
                type="file"
                accept="image/*"
                id="banner-upload"
                className="hidden"
                onChange={handleUpload}
                data-testid="banner-image-input"
              />
              <label
                htmlFor="banner-upload"
                className="cursor-pointer inline-flex items-center gap-2 bg-zinc-900 border border-zinc-800 hover:border-orange-600 px-3 py-2 text-sm text-zinc-300"
              >
                <Upload className="h-4 w-4" /> {uploading ? "Uploading…" : "Upload image"}
              </label>
              {form.image_url && (
                <button
                  type="button"
                  onClick={() => setForm({ ...form, image_url: "" })}
                  className="text-zinc-500 hover:text-red-400"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
            {form.image_url && (
              <img src={form.image_url} alt="preview" className="mt-2 max-h-32 border border-zinc-800" data-testid="banner-image-preview" />
            )}
            <FieldError message={errors.image_url} testId="banner-image-error" />
          </div>
          <div>
            <Label className="text-zinc-300">Destination URL *</Label>
            <Input
              className="bg-zinc-900 border-zinc-800 mt-1"
              value={form.link_url}
              onChange={(e) => update("link_url", e.target.value)}
              placeholder="https://yoursite.com"
              data-testid="banner-link-input"
            />
            <FieldError message={errors.link_url} testId="banner-link-error" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-zinc-300">Placement</Label>
              <Select value={form.placement} onValueChange={(v) => setForm({ ...form, placement: v })}>
                <SelectTrigger className="bg-zinc-900 border-zinc-800 mt-1" data-testid="banner-placement-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-zinc-950 border-zinc-800">
                  <SelectItem value="all">All pages</SelectItem>
                  <SelectItem value="hero">Homepage</SelectItem>
                  <SelectItem value="browse">Directory & search</SelectItem>
                  <SelectItem value="jobs">Job board</SelectItem>
                  <SelectItem value="profile">Profile pages</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-zinc-300">Contact Email</Label>
              <Input
                className="bg-zinc-900 border-zinc-800 mt-1"
                value={form.contact_email}
                onChange={(e) => update("contact_email", e.target.value)}
                placeholder="reports@yourco.com"
                data-testid="banner-contact-email-input"
              />
              <FieldError message={errors.contact_email} testId="banner-contact-email-error" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-zinc-300">Start Date</Label>
              <Input
                type="date"
                className="bg-zinc-900 border-zinc-800 mt-1"
                value={form.start_date}
                onChange={(e) => update("start_date", e.target.value)}
                data-testid="banner-start-date-input"
              />
            </div>
            <div>
              <Label className="text-zinc-300">End Date</Label>
              <Input
                type="date"
                className="bg-zinc-900 border-zinc-800 mt-1"
                value={form.end_date}
                onChange={(e) => update("end_date", e.target.value)}
                data-testid="banner-end-date-input"
              />
            </div>
          </div>
          <FieldError message={errors.dates} testId="banner-dates-error" />
          <p className="text-xs text-zinc-500">Price: R{AD_PRICE_HINT} per 30 days. You'll pay after submitting; your banner goes live once approved.</p>
        </div>
        <DialogFooter>
          <Button variant="outline" className="border-zinc-700" onClick={onClose}>
            Cancel
          </Button>
          <Button
            className="bg-orange-600 hover:bg-orange-500"
            onClick={handleSubmit}
            disabled={submitting}
            data-testid="submit-banner-btn"
          >
            {submitting ? "Submitting…" : "Submit for Review"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
